/**
 * ResultsTable — minimal unified results grid (§14.1 / DM-29).
 *
 * P1.3 (2026-08-14): мигрирована на единый `<DataTable>` — каркас
 * (pagination/pin/sort/filter/auto-scroll) делегирован; здесь только
 * динамические property-колонки + cell-рендер с confidence-bands.
 *
 * Block M1b (TZ 2026-07-29). Shows per-row:
 *   - row identity (по dataset row index)
 *   - category path (purple chip из M1a aggregate style)
 *   - determined property columns (dynamic, из properties_meta.presence)
 *   - confidence bands (DM-26 — green/orange/red)
 *
 * Unresolved rows are visible (DM-23) — never silently dropped.
 *
 * Source of truth: writer mirrors full `mapped_data` per row, so we read
 * per-row entry from `mapped_data.rows[row.id]` and the property keys from
 * `properties_meta.presence`.
 */
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { type ColumnDef } from '@tanstack/react-table';
import { Tag } from 'lucide-react';

import { usePipelineMatchingRows } from '@/hooks/useMatching';
import { DataTable } from '@/components/shared/DataTable';
import { cn } from '@/lib/utils';

export interface ResultsTableProps {
  pipelineId: string | null;
}

interface DerivedRow {
  row_id: string;
  row_index: number;
  status: string;
  path: string;
  property_columns: Array<{
    key: string;
    name: string;
    value: string;
    band: 'green' | 'orange' | 'red';
  }>;
}

function band(score: number, unresolved: boolean): 'green' | 'orange' | 'red' {
  if (unresolved) return 'red';
  if (score >= 0.7) return 'green';
  if (score >= 0.4) return 'orange';
  return 'red';
}

export function ResultsTable({ pipelineId }: ResultsTableProps) {
  const { t } = useTranslation();
  const { data: rowsResp } = usePipelineMatchingRows(pipelineId);

  // Pick a row that carries full mapped_data (write-path mirror).
  const sample = rowsResp?.rows?.[0];
  const mappedData = sample?.mapped_data as
    | {
        language?: 'ua' | 'ru';
        rows?: Record<
          string,
          {
            status?: string;
            category?: { path_ua?: string[]; path_ru?: string[] } | null;
            properties?: Record<
              string,
              { name?: string; values?: string[]; confidence?: number; status?: string }
            >;
          }
        >;
        properties_meta?: {
          presence?: Record<string, number>;
        };
      }
    | null
    | undefined;

  const propertyKeys = useMemo(
    () => Object.keys(mappedData?.properties_meta?.presence ?? {}),
    [mappedData],
  );

  const propertyNames = useMemo(() => {
    const m: Record<string, string> = {};
    if (!mappedData?.rows) return m;
    for (const entry of Object.values(mappedData.rows)) {
      for (const [k, v] of Object.entries(entry.properties ?? {})) {
        if (v?.name && !m[k]) m[k] = v.name;
      }
    }
    return m;
  }, [mappedData]);

  const rowsArr = rowsResp?.rows ?? [];
  const derived: DerivedRow[] = useMemo(() => {
    const out: DerivedRow[] = [];
    const md = mappedData;
    if (!md) return out;
    const lang: 'ua' | 'ru' = md.language === 'ru' ? 'ru' : 'ua';
    rowsArr.forEach((r, idx) => {
      const own = (r.mapped_data as typeof md | null | undefined)?.rows?.[r.id];
      if (!own) return;
      const pathArr = (lang === 'ru' ? own.category?.path_ru : own.category?.path_ua) ?? [];
      const path = pathArr.length > 0 ? pathArr.join(' / ') : '—';
      const status = own.status || 'unresolved';
      const unresolved = status === 'unresolved';
      out.push({
        row_id: r.id,
        row_index: idx + 1,
        status,
        path,
        property_columns: propertyKeys.map((k) => {
          const cell = own.properties?.[k];
          const value = (cell?.values ?? []).join(', ') || '—';
          const score = cell?.confidence ?? 0;
          return {
            key: k,
            name: propertyNames[k] ?? k,
            value,
            band: band(score, unresolved || cell?.status === 'unresolved'),
          };
        }),
      });
    });
    return out;
  }, [rowsArr, propertyKeys, propertyNames, mappedData]);

  const columns = useMemo<ColumnDef<DerivedRow>[]>(() => {
    const cols: ColumnDef<DerivedRow>[] = [
      {
        id: 'row',
        accessorKey: 'row_index',
        header: () =>
          t('process.matching.results.table.colRow', { defaultValue: 'Row' }),
        cell: (info) => (
          <span className="font-mono text-surface-400">
            #{info.getValue<number>()}
          </span>
        ),
        size: 70,
        enableColumnFilter: false,
      },
      {
        id: 'category',
        accessorKey: 'path',
        header: () =>
          t('process.matching.results.table.colCategory', { defaultValue: 'Category' }),
        cell: (info) => {
          const r = info.row.original;
          if (r.status === 'unresolved' || !r.path || r.path === '—') {
            return (
              <span className="text-danger italic">
                {t('process.matching.results.table.unresolvedLabel', {
                  defaultValue: 'unresolved',
                })}
              </span>
            );
          }
          return (
            <span className="inline-flex items-center gap-1 bg-primary-500/15 border border-primary-500/30 rounded-full px-2 py-0.5 text-primary-200">
              <Tag className="w-2.5 h-2.5 text-primary-300" />
              {r.path}
            </span>
          );
        },
        size: 240,
      },
    ];
    for (const k of propertyKeys) {
      const name = propertyNames[k] ?? k;
      cols.push({
        id: `prop_${k}`,
        accessorFn: (row) => row.property_columns.find((c) => c.key === k)?.value ?? '—',
        header: () => <span title={name}>{name}</span>,
        cell: (info) => {
          const col = info.row.original.property_columns.find((c) => c.key === k);
          const value = col?.value ?? '—';
          return (
            <span
              className={cn(
                'block max-w-[12rem] truncate',
                col?.band === 'green' && 'border-l-2 border-success-tint bg-success-tint/5 px-1 -ml-1',
                col?.band === 'orange' && 'border-l-2 border-warning-tint bg-warning-tint/5 px-1 -ml-1',
                col?.band === 'red' && 'border-l-2 border-danger-tint bg-danger-tint/5 px-1 -ml-1',
              )}
              title={value}
            >
              {value}
            </span>
          );
        },
        size: 180,
        enableColumnFilter: false,
      });
    }
    return cols;
  }, [propertyKeys, propertyNames, t]);

  if (!pipelineId || derived.length === 0) return null;

  return (
    <section className="space-y-3" data-testid="results-table">
      <header>
        <h2 className="text-sm font-semibold uppercase tracking-wider text-surface-400">
          {t('process.matching.results.table.title', { defaultValue: 'Results' })}
        </h2>
        <p className="text-xs text-surface-500">
          {t('process.matching.results.table.subtitle', {
            defaultValue:
              'Source rows + category + determined property values. Unresolved rows are visible.',
          })}
        </p>
      </header>

      {/* §C.6: виртуализация строк включена точечно — только здесь (таблица
          результатов сопоставления растёт до тысяч строк). В DataTable работает
          от 50 строк на странице; остальные потребители остаются на прежнем
          поведении (проп не передан → virtualizeRows = false). */}
      <DataTable columns={columns} data={derived} pinLeftCount={1} virtualizeRows />
    </section>
  );
}